import { isTerminalOrderStatus } from "@loot-os/fulfillment-core/order/state-machine";
import { Button, buttonVariants } from "@loot-os/ui/components/button";
import { Checkbox } from "@loot-os/ui/components/checkbox";
import { Input } from "@loot-os/ui/components/input";
import { Skeleton } from "@loot-os/ui/components/skeleton";
import { toast } from "@loot-os/ui/components/sonner";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@loot-os/ui/components/table";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate, useParams } from "@tanstack/react-router";
import { Check, ImageOff } from "lucide-react";
import { useState } from "react";
import { z } from "zod";

import { CopyButton } from "../../components/DetailFields";
import { DisabledReason } from "../../components/DisabledReason";
import { ErrorAlert } from "../../components/ErrorAlert";
import { OrderActivityCard } from "../../components/OrderActivityCard";
import { OrderAddressDialog } from "../../components/OrderAddressDialog";
import { OrderCancelDialog } from "../../components/OrderCancelDialog";
import { OrderCardsDialog } from "../../components/OrderCardsDialog";
import { OrderCustomerPanel } from "../../components/OrderCustomerPanel";
import { OrderTotalCostField, OrderTrackingField } from "../../components/OrderMetadataFields";
import { OrderNotesEditor } from "../../components/OrderNotesEditor";
import { OrderTag } from "../../components/OrderTag";
import { OrderTenantEventsNotice } from "../../components/OrderTenantEventsNotice";
import { OrderTransitionActions } from "../../components/OrderTransitionActions";
import { PaidCostCell } from "../../components/PaidCostCell";
import { centsToDollars, dashIfEmpty, formatDate } from "../../lib/format";
import {
  lineStatusTagClass,
  ORDER_ADDRESS_FIELDS,
  type OrderDetail,
  orderAddressField,
  orderAddressLines,
  packingSubstatusLabel,
  packingTagClass,
  statusTagText,
} from "../../lib/fulfillmentOrders";
import { FULFILLMENT_WIDE_PANEL_CLASS } from "../../lib/fulfillmentPanel";
import { readMarketplaceSnapshot } from "../../lib/marketplaceLine";
import {
  hasPendingPackableLine,
  isPackableLine,
  isPackingOpen,
  type OrderActionBlock,
  type OrderActionFacts,
  type OrderPageAction,
  orderActionBlock,
  packingHoldsShipment,
} from "../../lib/orderActions";
import { readLineDisplay } from "../../lib/orderLineDisplay";
import { useTRPC } from "../../lib/trpc";

/**
 * The items table's headings, in TCG Fulfill Order's order. Source and the `buy` columns are Loot
 * OS's own and come after Retool's; the `buy` columns are a vendor buy's facts, shown only when
 * some line has one: on a facility order they would be a column of dashes each. Retool's In Stock
 * has no source here yet.
 */
const LINE_COLUMNS = [
  { key: "select", label: "", buy: false },
  { key: "itemId", label: "Item ID", buy: false },
  { key: "name", label: "Name", buy: false },
  { key: "status", label: "Status", buy: false },
  { key: "image", label: "Card Image", buy: false },
  { key: "price", label: "Card Price", buy: false },
  { key: "details", label: "Details", buy: false },
  { key: "link", label: "Link", buy: false },
  { key: "slowShip", label: "Slow Ship", buy: false },
  { key: "source", label: "Source", buy: false },
  { key: "size", label: "Size", buy: true },
  { key: "priceBasis", label: "Price basis", buy: true },
  { key: "paid", label: "Paid", buy: true },
  { key: "refund", label: "Size refund", buy: true },
  { key: "cancel", label: "", buy: false },
] as const;

/** Whether a line carries anything a vendor buy records: a size, a quoted price, a link or a cost. */
function hasBuyFacts(line: OrderDetail["lines"][number]): boolean {
  const snapshot = readMarketplaceSnapshot(line.snapshot);
  return (
    line.itemSource === "MARKETPLACE" ||
    line.paidCostCents !== null ||
    line.refundAmountCents !== null ||
    snapshot.listingPrice !== undefined ||
    Boolean(snapshot.variantId ?? snapshot.variantValue)
  );
}

/**
 * Customs declare the items at 80% of the expected items cost, fulfillment fees included, as
 * boxed's TCG Fulfill Order does (`trans_itemsValue * 0.8`).
 */
const CUSTOMS_VALUE_SHARE = 0.8;

/**
 * One fulfillment order, laid out on TCG Fulfill Order's twelve columns so the two read the same
 * side by side: the header row (back, name, status, date, the order id, Ship Order and Cancel
 * Order), tracking and the type under it, the items, then the address with its copy buttons, the
 * city column with Generate Label and Mark Packed, and the money; notes and the account below.
 * The router reuses this page when only `$orderId` changes, so the children that hold form state
 * are keyed by the order id and a move to another order clears them.
 */
export function OrderPage() {
  const trpc = useTRPC();
  const { orderId } = useParams({ from: "/app/fulfillment/orders/$orderId" });
  const detail = useQuery(trpc.platform.fulfillment.orders.get.queryOptions({ id: orderId }));
  const queryClient = useQueryClient();
  // Ticked lines, held with the order they belong to: the router reuses this page when only
  // `$orderId` changes, and a tick on one order must not carry over to the next.
  const [ticked, setTicked] = useState<{ orderId: string; lineIds: string[] }>({
    orderId,
    lineIds: [],
  });
  const [cardsOpen, setCardsOpen] = useState<string | null>(null);
  const updateLines = useMutation(
    trpc.platform.fulfillment.orders.updateLines.mutationOptions({
      onSuccess: (_result, input) => {
        const packed = input.updates[0]?.status === "PACKED";
        const count = input.updates.length;
        toast.success(
          `${count} ${count === 1 ? "card" : "cards"} marked ${packed ? "packed" : "pending"}.`,
        );
        setTicked({ orderId: input.id, lineIds: [] });
        void queryClient.invalidateQueries({
          queryKey: trpc.platform.fulfillment.orders.pathKey(),
        });
      },
    }),
  );

  if (detail.isLoading) return <Skeleton className="h-96 w-full" />;
  // A typed or pasted id that names no order lands here: keep a way back to the list.
  if (detail.error) {
    return (
      <div className="flex flex-col items-start gap-3">
        <Link to="/fulfillment/orders" className={buttonVariants()}>
          Back To Orders
        </Link>
        <ErrorAlert message={detail.error.message} />
      </div>
    );
  }
  if (!detail.data) return null;

  const { order, lines, stockxLineIds, shippingCountries, customer, tenantEventRoute } =
    detail.data;
  // Cancel shows only on an open order. The server also lets any platform admin cancel a SHIPPED
  // one, which Retool limits to `canCancelShippedOrders`: whether that needs a fulfillment role
  // within platform admin is open (see `orders.cancel`).
  const isOpen = !isTerminalOrderStatus(order.status);
  const activeLines = lines.filter((line) => line.status !== "CANCELLED");
  // Retool's `trans_itemsValue`: live lines' value plus fulfillment fee, the list's expected cost.
  const itemsCost = order.expectedCostCents;
  const showBuyFacts = lines.some(hasBuyFacts);
  const lineColumns = LINE_COLUMNS.filter((column) => showBuyFacts || !column.buy);
  // What the order page actions' enable rules read (`orderActionBlock`, lib/orderActions.ts).
  const actionFacts: OrderActionFacts = {
    status: order.status,
    type: order.type,
    trackingNumber: order.trackingNumber,
    accountLocked: order.accountLocked,
    hasPendingLine: hasPendingPackableLine(lines),
  };
  const holdShipment = packingHoldsShipment(order, lines);
  const addressText = orderAddressLines(order.shippingAddress).join("\n");

  // Packing: offered only where the server's staff pack allows it (`isPackingOpen`).
  const packingOpen = isPackingOpen(order.status);
  const tickedIds = ticked.orderId === order.id ? ticked.lineIds : [];
  const packable = packingOpen ? lines.filter(isPackableLine) : [];
  const tickedLines = packable.filter((line) => tickedIds.includes(line.id));
  const toPack = tickedLines.filter((line) => line.status === "PENDING");
  const toUnpack = tickedLines.filter((line) => line.status === "PACKED");
  const allTicked = packable.length > 0 && tickedLines.length === packable.length;
  const recordOnly = lines.some((line) => line.status !== "CANCELLED" && !isPackableLine(line));
  const setTick = (lineId: string, on: boolean) =>
    setTicked({
      orderId: order.id,
      lineIds: on ? [...tickedIds, lineId] : tickedIds.filter((id) => id !== lineId),
    });
  const mark = (targets: typeof packable, status: "PACKED" | "PENDING") =>
    updateLines.mutate({
      id: order.id,
      updates: targets.map((line) => ({ lineId: line.id, status })),
    });

  const stockxLines = lines
    .filter((line) => stockxLineIds.includes(line.id))
    .map((line) => {
      const snapshot = readMarketplaceSnapshot(line.snapshot);
      const size = snapshot.variantValue ?? snapshot.variantId;
      const name = readLineDisplay(line.snapshot).name ?? line.itemId;
      return {
        id: line.id,
        label: size ? `${name} size ${size}` : name,
        chargedPriceCents: snapshot.chargedPriceCents,
        chargedUsShippingCents: snapshot.chargedUsShippingCents,
        chargedShippingSurchargeCents: snapshot.chargedShippingSurchargeCents,
      };
    });
  const [nameColumn, placeColumn] = ORDER_ADDRESS_FIELDS;
  const orderTitle = order.customerName ? `${order.customerName}'s Order` : "Order";

  return (
    <div className={`${FULFILLMENT_WIDE_PANEL_CLASS} flex flex-col gap-4`}>
      {/* Retool's header row, in its order, on one line where it fits; below that width it wraps
          rather than push Ship Order and Cancel Order off the page. The StockX paid costs and why
          Ship is held go under it, so a form never grows the header. */}
      <OrderTransitionActions
        key={`transitions-${order.id}`}
        order={order}
        holdShipment={holdShipment}
        stockxLines={stockxLines}
      >
        {({ buttons, details }) => (
          <div className="flex flex-col gap-3">
            <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
              <Link
                to="/fulfillment/orders"
                className={`${buttonVariants()} shrink-0 whitespace-nowrap`}
              >
                Back To Orders
              </Link>
              <h1 className="min-w-0 truncate text-lg font-bold" title={orderTitle}>
                {orderTitle}
              </h1>
              <span className="shrink-0 text-2xl font-bold">{order.status}</span>
              <span className="shrink-0 font-semibold whitespace-nowrap">
                {formatDate(order.createdAt)}
              </span>
              <OrderIdField key={order.id} orderId={order.id} />
              <div className="ml-auto flex shrink-0 items-start justify-end gap-2">
                {buttons}
                {isOpen ? (
                  <OrderCancelDialog
                    key={`cancel-${order.id}`}
                    orderId={order.id}
                    orderType={order.type}
                  />
                ) : null}
              </div>
            </div>
            {details}
            <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
              <div className="flex min-w-72 items-center gap-2">
                {order.trackingNumber ? (
                  <CopyButton text={order.trackingNumber} label="Copy tracking number" />
                ) : null}
                <OrderTrackingField
                  key={`tracking-${order.id}`}
                  orderId={order.id}
                  trackingNumber={order.trackingNumber}
                />
              </div>
              <div className="flex flex-col">
                {order.accountLocked ? (
                  <span className="font-bold text-destructive">ACCOUNT IS LOCKED!</span>
                ) : null}
                <span className="text-2xl font-bold">{order.type}</span>
              </div>
              <div className="ml-auto flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
                <span>
                  {[order.tenantName ?? order.tenantId, order.method].filter(Boolean).join(" · ")}
                </span>
                {order.packingSubstatus ? (
                  <OrderTag className={packingTagClass(order.packingSubstatus)}>
                    {packingSubstatusLabel(order.packingSubstatus)}
                  </OrderTag>
                ) : null}
              </div>
            </div>
          </div>
        )}
      </OrderTransitionActions>

      <OrderTenantEventsNotice route={tenantEventRoute} tenantName={order.tenantName} />

      <div className="flex flex-col rounded-md border">
        {/* Retool's items table is a fixed height that scrolls its own rows. The height goes on the
            table's own scroll container, so the header sticks within the one box that scrolls
            both ways. */}
        <Table containerClassName="max-h-[31rem] overflow-auto">
          <TableHeader className="sticky top-0 z-10 bg-background shadow-[inset_0_-1px_0_var(--border)]">
            <TableRow>
              {lineColumns.map((column) =>
                column.key === "select" ? (
                  <TableHead key={column.key} className="w-8">
                    <Checkbox
                      aria-label="Select every card that can be packed"
                      checked={allTicked}
                      indeterminate={tickedLines.length > 0 && !allTicked}
                      disabled={packable.length === 0 || updateLines.isPending}
                      onCheckedChange={(on) =>
                        setTicked({
                          orderId: order.id,
                          lineIds: on ? packable.map((line) => line.id) : [],
                        })
                      }
                    />
                  </TableHead>
                ) : (
                  <TableHead key={column.key}>{column.label}</TableHead>
                ),
              )}
            </TableRow>
          </TableHeader>
          <TableBody>
            {lines.map((line) => {
              const snapshot = readMarketplaceSnapshot(line.snapshot);
              const display = readLineDisplay(line.snapshot);
              const cancelled = line.status === "CANCELLED";
              return (
                <TableRow key={line.id} className={cancelled ? "opacity-60" : undefined}>
                  <TableCell>
                    {packable.includes(line) ? (
                      <Checkbox
                        aria-label={`Select ${display.name ?? line.itemId}`}
                        checked={tickedIds.includes(line.id)}
                        disabled={updateLines.isPending}
                        onCheckedChange={(on) => setTick(line.id, on)}
                      />
                    ) : null}
                  </TableCell>
                  <TableCell className="font-mono text-xs">{line.itemId}</TableCell>
                  {/* One line, cut off as Retool's is; the whole name is the cell's tooltip. */}
                  <TableCell
                    className="max-w-[30rem] truncate font-medium"
                    title={lineName(display)}
                  >
                    {lineName(display)}
                  </TableCell>
                  <TableCell>
                    <OrderTag className={lineStatusTagClass(line.status)}>
                      {statusTagText(line.status)}
                    </OrderTag>
                  </TableCell>
                  <TableCell>
                    {display.imageUrl ? (
                      <img
                        src={display.imageUrl}
                        alt=""
                        loading="lazy"
                        className="h-12 w-9 rounded-sm object-contain"
                      />
                    ) : (
                      <div className="flex h-12 w-9 items-center justify-center rounded-sm bg-muted text-muted-foreground">
                        <ImageOff className="size-4" />
                      </div>
                    )}
                  </TableCell>
                  <TableCell className="tabular-nums">{centsToDollars(line.unitValue)}</TableCell>
                  <TableCell>{dashIfEmpty(display.printing)}</TableCell>
                  <TableCell>
                    {snapshot.productUrl ? (
                      <a
                        className="underline underline-offset-4"
                        href={snapshot.productUrl}
                        target="_blank"
                        rel="noreferrer"
                      >
                        Open
                      </a>
                    ) : (
                      "—"
                    )}
                  </TableCell>
                  <TableCell>{line.isSlowShip ? <Check className="size-4" /> : null}</TableCell>
                  <TableCell>{line.itemSource}</TableCell>
                  {showBuyFacts ? (
                    <>
                      <TableCell>
                        {dashIfEmpty(snapshot.variantValue ?? snapshot.variantId)}
                      </TableCell>
                      <TableCell className="tabular-nums">
                        {snapshot.listingPrice === undefined
                          ? "—"
                          : centsToDollars(snapshot.listingPrice)}
                      </TableCell>
                      <TableCell className="tabular-nums">
                        <PaidCostCell
                          paidCostCents={line.paidCostCents}
                          paidCostParts={line.paidCostParts}
                        />
                      </TableCell>
                      <TableCell className="tabular-nums">
                        {line.refundAmountCents === null
                          ? "—"
                          : centsToDollars(line.refundAmountCents)}
                      </TableCell>
                    </>
                  ) : null}
                  <TableCell className="text-right">
                    {isOpen && !cancelled ? (
                      <OrderCancelDialog
                        orderId={order.id}
                        orderType={order.type}
                        line={{ id: line.id, isLastActive: activeLines.length === 1 }}
                      />
                    ) : null}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
        <div className="flex items-center justify-between gap-3 border-t px-2 py-1.5 text-sm">
          <Button type="button" variant="ghost" size="sm" onClick={() => setCardsOpen(order.id)}>
            Cards View
          </Button>
          <span className="text-muted-foreground">
            {lines.length} {lines.length === 1 ? "result" : "results"}
          </span>
        </div>
      </div>
      {showBuyFacts || recordOnly || packable.length > 0 || !packingOpen ? (
        <p className="-mt-2 text-xs text-muted-foreground">
          {[
            packingOpen && packable.length > 0
              ? "Packing a card takes it off the shelf. Marking it pending again puts it back."
              : null,
            showBuyFacts
              ? "Price basis is what the tenant quoted its shopper; a live price above it is refused."
              : null,
            packingOpen ? null : `Cards can't be packed once the order is ${order.status}.`,
            recordOnly
              ? "A card with no checkbox is recorded from the tenant's own system and is packed there."
              : null,
          ]
            .filter(Boolean)
            .join(" ")}
        </p>
      ) : null}
      <OrderCardsDialog
        orderIds={cardsOpen === order.id ? [order.id] : null}
        onClose={() => setCardsOpen(null)}
      />

      <div className="grid gap-x-4 gap-y-6 lg:grid-cols-12">
        <section className="flex flex-col gap-2 lg:col-span-4">
          <div className="flex items-center gap-3">
            <h2 className="text-2xl font-bold">Address</h2>
            <OrderAddressDialog
              key={order.id}
              orderId={order.id}
              address={order.shippingAddress}
              shippingCountries={shippingCountries}
              disabledReason={actionBlockText("editAddress", actionFacts)}
            />
            {addressText ? <CopyButton text={addressText} label="Copy address" /> : null}
          </div>
          <dl className="flex flex-col gap-1">
            {nameColumn.map(([key, label]) => (
              <AddressField
                key={key}
                label={label}
                value={orderAddressField(order.shippingAddress, key)}
              />
            ))}
          </dl>
        </section>

        <section className="flex flex-col gap-2 lg:col-span-4">
          <dl className="flex flex-col gap-1">
            {placeColumn.map(([key, label]) => (
              <AddressField
                key={key}
                label={label}
                value={orderAddressField(order.shippingAddress, key)}
              />
            ))}
          </dl>
          <div className="grid grid-cols-2 gap-2 pt-1">
            <NotBuiltButton label="Generate Label" action="generateLabel" facts={actionFacts} />
            <DisabledReason
              reason={markPackedBlock(packable.length, tickedLines.length, toPack.length)}
            >
              <Button
                type="button"
                className="w-full"
                disabled={toPack.length === 0 || updateLines.isPending}
                onClick={() => mark(toPack, "PACKED")}
              >
                Mark Packed{toPack.length ? ` (${toPack.length})` : ""}
              </Button>
            </DisabledReason>
            <Button
              type="button"
              variant="outline"
              className="col-start-2"
              disabled={toUnpack.length === 0 || updateLines.isPending}
              onClick={() => mark(toUnpack, "PENDING")}
            >
              Mark Pending{toUnpack.length ? ` (${toUnpack.length})` : ""}
            </Button>
          </div>
        </section>

        <section className="flex flex-col gap-1 text-sm lg:col-span-4">
          {order.cancelledAt ? <p>Cancelled At: {formatDate(order.cancelledAt)}</p> : null}
          {order.cancellationReason ? (
            <p>
              <span className="font-semibold">Cancellation Reason:</span> {order.cancellationReason}
              {order.cancellationCode ? ` (${order.cancellationCode})` : ""}
            </p>
          ) : null}
          <p>Total Value: {centsToDollars(itemsCost + order.shippingFee)}</p>
          <p>Shipping: {centsToDollars(order.shippingFee)}</p>
          {order.vendorId ? (
            <p>
              Vendor: <span className="font-mono text-xs">{order.vendorId}</span>
            </p>
          ) : null}
          <p>Expected Items Cost: {centsToDollars(itemsCost)}</p>
          <p>Customs Value: {centsToDollars(Math.round(itemsCost * CUSTOMS_VALUE_SHARE))}</p>
          <OrderTotalCostField
            key={`cost-${order.id}`}
            orderId={order.id}
            totalCost={order.totalCost}
          />
          <div className="grid pt-2">
            <NotBuiltButton label="Upload Image" action="uploadImage" facts={actionFacts} />
          </div>
        </section>
      </div>

      <div className="grid gap-x-4 gap-y-6 lg:grid-cols-12">
        <div className="flex flex-col gap-6 lg:col-span-6">
          <OrderNotesEditor key={order.id} orderId={order.id} notes={order.notes} />
          <OrderCustomerPanel
            order={order}
            customer={customer}
            tenantOwned={tenantEventRoute !== null}
          />
          <div>
            <Button type="button" onClick={() => toast("Generate Invoice is not built yet.")}>
              Generate Invoice
            </Button>
          </div>
        </div>
        <div className="lg:col-span-6">
          <OrderActivityCard
            orderId={order.id}
            lineNames={Object.fromEntries(
              lines.map((line) => [line.id, readLineDisplay(line.snapshot).name ?? line.itemId]),
            )}
          />
        </div>
      </div>
    </div>
  );
}

/** A line's "name - collection", as TCG Fulfill Order's Name column prints it. */
function lineName(display: ReturnType<typeof readLineDisplay>): string {
  const name = display.name ?? "Unnamed item";
  return display.collectionName ? `${name} - ${display.collectionName}` : name;
}

/**
 * A Loot OS order id, the only thing the Order Id box opens, checked as the server checks it
 * (`orders.get` takes `z.uuid()`), so an id one digit off never reaches the error page.
 */
const ORDER_ID = z.uuid();

/**
 * TCG Fulfill Order's Order Id box with its copy button. As there, typing another order's id
 * opens that order, here on Enter only, and only a whole id: half an id would open an error page
 * with no way back but the browser's. Leaving the box puts the open order's id back.
 */
function OrderIdField({ orderId }: { orderId: string }) {
  const navigate = useNavigate();
  const [draft, setDraft] = useState(orderId);
  const open = () => {
    const next = draft.trim();
    if (next === orderId) return;
    if (!ORDER_ID.safeParse(next).success) {
      toast.error("That isn't a valid order id.");
      return;
    }
    void navigate({ to: "/fulfillment/orders/$orderId", params: { orderId: next } });
  };
  return (
    <div className="flex shrink-0 items-center gap-2">
      <label htmlFor="order-id" className="text-sm font-semibold whitespace-nowrap">
        Order Id
      </label>
      <Input
        id="order-id"
        className="w-72 font-mono text-xs"
        value={draft}
        onChange={(event) => setDraft(event.target.value)}
        onBlur={() => setDraft(orderId)}
        onKeyDown={(event) => {
          if (event.key === "Enter") open();
        }}
      />
      <CopyButton text={orderId} label="Copy order id" />
    </div>
  );
}

/**
 * One "Label: **value**" row of the address with its own copy button, as TCG Fulfill Order prints
 * it: a fulfiller pastes the fields one at a time into a carrier's form.
 */
function AddressField({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center gap-2 text-lg">
      <dt>{label}:</dt>
      <dd className="flex items-center gap-2 font-semibold">
        {value || "—"}
        {value ? <CopyButton text={value} label={`Copy ${label.toLowerCase()}`} /> : null}
      </dd>
    </div>
  );
}

/**
 * A TCG Fulfill Order button not built yet, placed with Retool's enable rule so the page's shape
 * is settled; it says so when pressed. Ship, cancel and the edits are real.
 */
function NotBuiltButton({
  label,
  action,
  facts,
}: {
  label: string;
  action: OrderPageAction;
  facts: OrderActionFacts;
}) {
  const block = actionBlockText(action, facts);
  return (
    <DisabledReason reason={block}>
      <Button
        type="button"
        className="w-full"
        disabled={block !== undefined}
        onClick={() => toast(`${label} is not built yet.`)}
      >
        {label}
      </Button>
    </DisabledReason>
  );
}

/** Why Mark Packed is off, by the cause: nothing here can be packed, only packed cards are ticked, or nothing is ticked. */
function markPackedBlock(packable: number, ticked: number, toPack: number): string | undefined {
  if (toPack > 0) return undefined;
  if (packable === 0) return "No card on this order can be packed here.";
  if (ticked > 0) return "The ticked cards are already packed; Mark Pending unpacks them.";
  return "Tick the cards to pack in the table above.";
}

/** Why `action` is off for this order, as its button's tooltip says it; undefined when it is on. */
function actionBlockText(action: OrderPageAction, facts: OrderActionFacts): string | undefined {
  const block = orderActionBlock(action, facts);
  return block === null ? undefined : ACTION_BLOCK_TEXT[block];
}

/** Why a placed action is off, as the button's tooltip says it. */
const ACTION_BLOCK_TEXT: Record<OrderActionBlock, string> = {
  status: "Not in this order's status.",
  has_tracking: "The order already has a tracking number.",
  account_locked: "The account is locked.",
  pending_tcg_line: "A card is still pending: pack it first.",
};
